import { UserProfile } from '@shared/types/user';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { ResourceService } from '../services/resource.service';
import {
  CreateResourceData,
  Resource,
  ResourceSortType,
  ReviewResourceData,
  UpdateResourceData
} from '../types/resource.types';

interface ResourcesState {
  // Resources list
  resources: Resource[];
  totalResources: number;
  currentPage: number;
  hasNextPage: boolean;
  isLoading: boolean;
  error: string | null;
  sortType: ResourceSortType;

  // Current resource
  currentResource: Resource | null;
  isLoadingCurrent: boolean;
  currentError: string | null;

  // Search
  searchResults: Resource[];
  totalSearchResults: number;
  currentSearchPage: number;
  hasNextSearchPage: boolean;
  isSearching: boolean;
  searchError: string | null;
  searchQuery: string;

  // Admin management
  adminResources: Resource[];
  totalAdminResources: number;
  adminCurrentPage: number;
  isLoadingAdmin: boolean;
  adminError: string | null;

  // My resources (non-admin)
  myResources: Resource[];
  totalMyResources: number;
  myCurrentPage: number;
  isLoadingMyResources: boolean;
  myResourcesError: string | null;

  // Actions
  fetchResources: (page?: number, limit?: number) => Promise<void>;
  fetchResource: (resourceId: string) => Promise<void>;
  createResource: (data: CreateResourceData) => Promise<Resource | null>;
  updateResource: (resourceId: string, data: UpdateResourceData) => Promise<Resource | null>;
  deleteResource: (resourceId: string) => Promise<boolean>;
  searchResources: (query: string, page?: number, limit?: number) => Promise<void>;
  setSortType: (sortType: ResourceSortType) => void;
  fetchAdminResources: (page?: number, limit?: number) => Promise<void>;
  fetchMyResources: (page?: number, limit?: number) => Promise<void>;
  reviewResource: (resourceId: string, data: ReviewResourceData, reviewer?: Partial<UserProfile>) => Promise<Resource | null>;
  resetState: () => void;
}

export const useResourcesStore = create<ResourcesState>()(
  devtools(
    (set, get) => ({
      // Resources list
      resources: [],
      totalResources: 0,
      currentPage: 1,
      hasNextPage: false,
      isLoading: false,
      error: null,
      sortType: ResourceSortType.NEWEST,

      // Current resource
      currentResource: null,
      isLoadingCurrent: false,
      currentError: null,

      // Search
      searchResults: [],
      totalSearchResults: 0,
      currentSearchPage: 1,
      hasNextSearchPage: false,
      isSearching: false,
      searchError: null,
      searchQuery: '',

      // Admin management
      adminResources: [],
      totalAdminResources: 0,
      adminCurrentPage: 1,
      isLoadingAdmin: false,
      adminError: null,

      // My resources
      myResources: [],
      totalMyResources: 0,
      myCurrentPage: 1,
      isLoadingMyResources: false,
      myResourcesError: null,

      // Actions
      fetchResources: async (page = 1, limit = 10) => {
        try {
          set({ isLoading: true, error: null });
          const response = await ResourceService.getResources(
            page,
            limit,
            get().sortType
          );

          set({
            resources: response.resources,
            totalResources: response.total,
            currentPage: page,
            hasNextPage: response.hasNextPage,
            isLoading: false,
          });
        } catch (error) {
          set({
            isLoading: false,
            error:
              error instanceof Error ? error.message : 'Error fetching resources',
          });
        }
      },

      fetchResource: async (resourceId: string) => {
        try {
          set({ isLoadingCurrent: true, currentError: null });
          const resource = await ResourceService.getResource(resourceId);

          if (resource) {
            set({
              currentResource: resource,
              isLoadingCurrent: false,
            });
          } else {
            set({
              isLoadingCurrent: false,
              currentError: 'Resource not found',
            });
          }
        } catch (error) {
          set({
            isLoadingCurrent: false,
            currentError:
              error instanceof Error ? error.message : 'Error fetching resource',
          });
        }
      },

      createResource: async (data: CreateResourceData) => {
        try {
          set({ isLoading: true, error: null });
          const resource = await ResourceService.createResource(data);

          if (resource) {
            // Add the new resource to the list if we're on the first page
            if (get().currentPage === 1) {
              set((state) => ({
                resources: [resource, ...state.resources],
                totalResources: state.totalResources + 1,
              }));
            }

            set({ isLoading: false });
          }

          return resource;
        } catch (error) {
          set({
            isLoading: false,
            error:
              error instanceof Error ? error.message : 'Error creating resource',
          });
          return null;
        }
      },

      updateResource: async (resourceId: string, data: UpdateResourceData) => {
        try {
          set({ isLoading: true, error: null });
          const updatedResource = await ResourceService.updateResource(resourceId, data);

          if (updatedResource) {
            // Update the resource in the list
            set((state) => ({
              resources: state.resources.map((r) =>
                r.id === resourceId ? updatedResource : r
              ),
              // Update current resource if it's the one being edited
              currentResource:
                state.currentResource?.id === resourceId
                  ? updatedResource
                  : state.currentResource,
              isLoading: false,
            }));
          }

          return updatedResource;
        } catch (error) {
          set({
            isLoading: false,
            error:
              error instanceof Error ? error.message : 'Error updating resource',
          });
          return null;
        }
      },

      deleteResource: async (resourceId: string) => {
        try {
          set({ isLoading: true, error: null });
          const success = await ResourceService.deleteResource(resourceId);

          if (success) {
            // Remove the deleted resource from the list
            set((state) => ({
              resources: state.resources.filter((r) => r.id !== resourceId),
              totalResources: state.totalResources - 1,
              // Reset current resource if it's the one being deleted
              currentResource:
                state.currentResource?.id === resourceId
                  ? null
                  : state.currentResource,
              isLoading: false,
            }));
          }

          return success;
        } catch (error) {
          set({
            isLoading: false,
            error:
              error instanceof Error ? error.message : 'Error deleting resource',
          });
          return false;
        }
      },

      searchResources: async (query: string, page = 1, limit = 10) => {
        try {
          set({ isSearching: true, searchError: null, searchQuery: query });
          const response = await ResourceService.searchResources(
            query,
            page,
            limit
          );

          set({
            searchResults: response.resources,
            totalSearchResults: response.total,
            currentSearchPage: page,
            hasNextSearchPage: response.hasNextPage,
            isSearching: false,
          });
        } catch (error) {
          set({
            isSearching: false,
            searchError:
              error instanceof Error ? error.message : 'Error searching resources',
          });
        }
      },

      setSortType: (sortType: ResourceSortType) => {
        set({ sortType });
        get().fetchResources(1);
      },

      fetchAdminResources: async (page = 1, limit = 20) => {
        try {
          set({ isLoadingAdmin: true, adminError: null });
          const response = await ResourceService.getAdminResources(page, limit);
          set({
            adminResources: response.resources,
            totalAdminResources: response.total,
            adminCurrentPage: page,
            isLoadingAdmin: false,
          });
        } catch (error) {
          set({
            isLoadingAdmin: false,
            adminError: error instanceof Error ? error.message : 'Error fetching resources',
          });
        }
      },

      fetchMyResources: async (page = 1, limit = 20) => {
        try {
          set({ isLoadingMyResources: true, myResourcesError: null });
          const response = await ResourceService.getMyResources(page, limit);
          set({
            myResources: response.resources,
            totalMyResources: response.total,
            myCurrentPage: page,
            isLoadingMyResources: false,
          });
        } catch (error) {
          set({
            isLoadingMyResources: false,
            myResourcesError: error instanceof Error ? error.message : 'Error fetching your resources',
          });
        }
      },

      reviewResource: async (resourceId: string, data: ReviewResourceData, reviewer?: Partial<UserProfile>) => {
        try {
          const updated = await ResourceService.reviewResource(resourceId, data);
          if (updated) {
            set((state) => ({
              adminResources: state.adminResources.map((r) =>
                r.id === resourceId
                  ? { ...updated, reviewedByProfile: reviewer ?? updated.reviewedByProfile }
                  : r
              ),
            }));
          }
          return updated;
        } catch (error) {
          return null;
        }
      },

      resetState: () => {
        set({
          resources: [],
          totalResources: 0,
          currentPage: 1,
          hasNextPage: false,
          currentResource: null,
          error: null,
          currentError: null,
          searchResults: [],
          totalSearchResults: 0,
          currentSearchPage: 1,
          hasNextSearchPage: false,
          searchError: null,
          searchQuery: '',
        });
      },
    }),
    { name: 'resources-store' }
  )
);
